export interface AgencyFile {
  fileId: number;
  fileName: string;
  mimeType: string;
}
export interface AgencyVersion extends AgencyFile {
  id: number;
  number: number;
  size: number;
  issueDate: string | null;
  effectiveDate: string | null;
  expiresOn: string | null;
  doesNotExpire: boolean;
  confirmedAt: string | null;
  uploadedByName: string;
  createdAt?: string;
  status: string;
  isCurrent: boolean;
  signedEvidence: (AgencyFile & { id: number; createdAt: string }) | null;
}
export interface AgencyDocument {
  id: number;
  agencyId: number;
  title: string;
  category: string;
  partnershipId?: number | null;
  partnership?: { id: number; institutionName: string } | null;
  requiresSignature?: boolean;
  archivedAt: string | null;
  versions: AgencyVersion[];
  currentVersion: AgencyVersion | null;
  latestVersion: AgencyVersion | null;
  status: string;
  matchingVersion?: AgencyVersion | null;
  events: {
    id: number;
    kind: string;
    actorName: string;
    details: unknown;
    createdAt: string;
    fileId?: number | null;
    fileName?: string | null;
    mimeType?: string | null;
  }[];
  pagination: {
    versionPage: number;
    eventPage: number;
    versionPages: number;
    eventPages: number;
  };
}
export interface AgencyRegister {
  agency: {
    id: number;
    name: string;
    country: string | null;
    countryIssue?: string | null;
  };
  capabilities: {
    canManage: boolean;
    canIssueContracts: boolean;
    canConfirmSigned: boolean;
    canManageAccess: boolean;
    canManageScope?: boolean;
    canReturnSigned?: boolean;
  };
  isAdmin: boolean;
  partnerships?: {
    id: number;
    institutionName?: string;
    universities?: { name: string };
  }[];
  documents: AgencyDocument[];
  grants: {
    userId: number;
    firstName: string;
    lastName: string;
    email: string;
    isActive: boolean;
    canView: boolean;
    canManage: boolean;
  }[];
  pagination: { page: number; total: number; pages: number };
}
export interface AgencyList {
  agencies: {
    id: number;
    name: string;
    country: string | null;
    contractCount: number;
    matchingContractCount: number;
    contracts: AgencyDocument[];
  }[];
  page: number;
  total: number;
  pages: number;
}
export interface AgencyDocumentsApi {
  register(
    agency: number | undefined,
    page: number,
    signal: AbortSignal,
    includeArchived?: boolean,
    filters?: { search?: string; category?: string; partnershipId?: number },
  ): Promise<AgencyRegister>;
  detail(
    id: number,
    versionPage: number,
    eventPage: number,
    signal: AbortSignal,
  ): Promise<AgencyDocument>;
  list(
    filters: Record<string, string | number>,
    signal: AbortSignal,
  ): Promise<AgencyList>;
  upload(
    agency: number,
    body: FormData,
    document?: number,
  ): Promise<AgencyDocument>;
  signed(version: number, body: FormData): Promise<AgencyDocument>;
  confirm(
    version: number,
    operationId: string,
    signedEventId: number,
  ): Promise<AgencyDocument>;
  archive(
    document: number,
    operationId: string,
    reason: string,
  ): Promise<AgencyDocument>;
  grant(
    agency: number,
    user: number,
    operationId: string,
    access: string,
  ): Promise<AgencyRegister>;
  reject?(
    version: number,
    operationId: string,
    signedEventId: number,
    reason: string,
  ): Promise<AgencyDocument>;
  settings?(
    document: number,
    settings: {
      partnershipId: number | null;
      requiresSignature: boolean;
      operationId: string;
    },
  ): Promise<AgencyDocument>;
  file(file: AgencyFile, download?: boolean): Promise<Blob>;
}
export const agencyDocumentsApi = (api: any): AgencyDocumentsApi => {
  const base = "/agency-documents";
  return {
    register: async (
      agency,
      page,
      signal,
      includeArchived = false,
      filters = {},
    ) =>
      (
        await api.get(agency ? `${base}/agencies/${agency}` : `${base}/mine`, {
          params: {
            page,
            ...(filters.search ? { search: filters.search } : {}),
            ...(filters.category ? { category: filters.category } : {}),
            ...(filters.partnershipId
              ? { partnershipId: filters.partnershipId }
              : {}),
            ...(includeArchived ? { includeArchived: "true" } : {}),
          },
          signal,
        })
      ).data.data,
    detail: async (id, versionPage, eventPage, signal) =>
      (
        await api.get(`${base}/documents/${id}`, {
          params: { versionPage, eventPage },
          signal,
        })
      ).data.data,
    list: async (params, signal) =>
      (await api.get(`${base}/agencies`, { params, signal })).data.data,
    upload: async (agency, body, document) =>
      (
        await api.post(
          document
            ? `${base}/documents/${document}/versions`
            : `${base}/agencies/${agency}/documents`,
          body,
          { headers: { "Content-Type": "multipart/form-data" } },
        )
      ).data.data,
    signed: async (version, body) =>
      (
        await api.post(`${base}/versions/${version}/signed`, body, {
          headers: { "Content-Type": "multipart/form-data" },
        })
      ).data.data,
    confirm: async (version, operationId, signedEventId) =>
      (
        await api.post(`${base}/versions/${version}/confirm`, {
          operationId,
          signedEventId,
        })
      ).data.data,
    archive: async (document, operationId, reason) =>
      (
        await api.post(`${base}/documents/${document}/archive`, {
          operationId,
          reason,
        })
      ).data.data,
    grant: async (agency, user, operationId, access) =>
      (
        await api.put(`${base}/agencies/${agency}/grants/${user}`, {
          operationId,
          canView: access !== "none",
          canManage: access === "manage",
        })
      ).data.data,
    reject: async (version, operationId, signedEventId, reason) =>
      (
        await api.post(`${base}/versions/${version}/reject`, {
          operationId,
          signedEventId,
          reason,
        })
      ).data.data,
    settings: async (document, settings) =>
      (await api.post(`${base}/documents/${document}/settings`, settings)).data
        .data,
    file: async (file, download) =>
      (
        await api.get(`${base}/files/${file.fileId}`, {
          responseType: "blob",
          params: download ? { download: 1 } : {},
        })
      ).data,
  };
};
